import { useEffect, useRef, useState } from 'react'
import {
  ActionIcon,
  Button,
  Combobox,
  Group,
  Loader,
  Skeleton,
  Text,
  TextInput,
  useCombobox,
} from '@mantine/core'
import { useDebouncedCallback } from '@mantine/hooks'
import { useTranslation } from 'react-i18next'
import i18n from '@/i18n'
import { IconAlertCircle, IconCheck, IconPlus, IconSearch, IconSearchOff, IconX } from '@tabler/icons-react'
import { fetchAdvertiserDetails, fetchAdvertiserSuggestions } from '../../services/api'
import classes from '../../styles/Generation.module.css'

/** A search's Places session token (AD-133): a fresh UUID per search. */
function newSessionToken() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const random = Math.floor(Math.random() * 16)
    return (char === 'x' ? random : ((random % 4) + 8)).toString(16)
  })
}

/** The advertiser of a chosen place, in the shape the form keeps (place_id, address, …). */
export function advertiserOfDetails(details) {
  return {
    place_id: details.placeId,
    name: details.name,
    address: details.formattedAddress || [details.address?.street, [details.address?.zip, details.address?.city].filter(Boolean).join(' ')].filter(Boolean).join(', '),
    website: details.website || '',
    phone: details.phone || '',
    category: details.category || '',
    manual: false,
  }
}

export function placeLabel(place) {
  return place?.name || place?.description || i18n.t('editorV2.generation.advertiser.label')
}

function placeInitial(place) {
  const label = placeLabel(place).trim()
  return label ? label[0].toUpperCase() : '?'
}

/**
 * Round brand mark (42px in search rows; smaller on the chosen advertiser and the Recent chips).
 * Google Places returns no logo, so the image is the advertiser website's favicon/logo served
 * (cached) by /advertiser-logo. A search row has no website yet: it shows Google's category
 * icon (PlaceCategoryIcon); without either it shows the initial.
 */
export function AdvertiserAvatar({ place, apiBase, size = 42 }) {
  const [failed, setFailed] = useState(false)
  const website = place?.website || ''
  const src = website && apiBase
    ? `${apiBase}/advertiser-logo?website=${encodeURIComponent(website)}`
    : null
  useEffect(() => { setFailed(false) }, [src])
  const icon = (!src || failed) && place?.icon?.url ? place.icon : null
  return (
    <span
      className={classes.avatar}
      style={{ width: size, height: size, ...(icon ? { background: icon.color, borderColor: icon.color } : {}) }}
      aria-hidden="true"
    >
      {src && !failed ? (
        <img src={src} alt="" loading="lazy" onError={() => setFailed(true)} />
      ) : icon ? (
        <img className={classes.avatarIcon} src={icon.url} alt="" loading="lazy" />
      ) : (
        <span className={classes.avatarInitial} style={size < 42 ? { fontSize: Math.round(size * 0.45) } : undefined}>
          {placeInitial(place)}
        </span>
      )}
    </span>
  )
}

/**
 * Advertiser search of the "ad generation with AI" component (Figma 84:4170, states search /
 * filled / not found): a search field with a clear button, result rows with a brand mark,
 * name and grey address and a check on the chosen advertiser, and a "No advertiser found"
 * panel that offers to add the advertiser by hand. Typing does not drop the current choice
 * until another row (or the clear button) replaces it.
 */
export function AdvertiserSearch({
  value,
  onSelect,
  onClear,
  onAddNew,
  addLabel,
  apiBase,
  language,
  disabled,
  label,
  placeholder,
  compact = false,
  error,
}) {
  const { t } = useTranslation()
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  })
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [state, setState] = useState('idle') // idle | loading | results | empty | error
  const abortRef = useRef(null)
  const latestQuery = useRef('')
  const sessionRef = useRef(null)

  const runSearch = async (input) => {
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller
    latestQuery.current = input
    setState('loading')
    combobox.openDropdown()
    try {
      if (!sessionRef.current) sessionRef.current = newSessionToken()
      const items = await fetchAdvertiserSuggestions(apiBase, input, language, sessionRef.current, controller.signal)
      if (controller.signal.aborted || latestQuery.current !== input) return
      setResults(items.map((item) => ({
        place_id: item.placeId,
        name: item.name,
        description: item.description,
        icon: item.icon || null,
      })))
      setState(items.length ? 'results' : 'empty')
      combobox.openDropdown()
    } catch {
      if (controller.signal.aborted) return
      setResults([])
      setState('error')
    }
  }

  const search = useDebouncedCallback((input) => { runSearch(input) }, 300)

  useEffect(() => () => abortRef.current?.abort(), [])

  // A language change invalidates both an in-flight request and its displayed matches.
  useEffect(() => {
    abortRef.current?.abort()
    search.cancel?.()
    setResults([])
    setState('idle')
    const input = query.trim()
    if (input.length >= 2) search(input)
  }, [language]) // eslint-disable-line react-hooks/exhaustive-deps

  // Pre-highlight the top match so Enter selects it.
  useEffect(() => {
    if (state === 'results') combobox.selectFirstOption()
  }, [results, state]) // eslint-disable-line react-hooks/exhaustive-deps

  const reset = () => {
    abortRef.current?.abort()
    search.cancel?.()
    setQuery('')
    setResults([])
    setState('idle')
    combobox.closeDropdown()
  }

  const handleChange = (event) => {
    const next = event.currentTarget.value
    setQuery(next)
    const trimmed = next.trim()
    if (trimmed.length < 2) {
      abortRef.current?.abort()
      setResults([])
      setState('idle')
      combobox.closeDropdown()
      return
    }
    search(trimmed)
  }

  // The details of the chosen place close the search's session; the next search starts another.
  // Typing again cancels the choice (same abort controller as the search), so a late answer
  // never selects a place over what the user typed since.
  const choose = async (place) => {
    abortRef.current?.abort()
    search.cancel?.()
    const controller = new AbortController()
    abortRef.current = controller
    const session = sessionRef.current || newSessionToken()
    sessionRef.current = null
    setState('loading')
    try {
      const details = await fetchAdvertiserDetails(apiBase, place.place_id, language, session, controller.signal)
      if (controller.signal.aborted) return
      onSelect(advertiserOfDetails(details))
      reset()
    } catch {
      if (controller.signal.aborted) return
      setState('error')
      combobox.openDropdown()
    }
  }

  const clearAll = () => {
    onClear()
    reset()
  }

  const display = query || (value ? placeLabel(value) : '')
  const showClear = Boolean(display) && !disabled

  return (
    <Combobox
      store={combobox}
      position="bottom-start"
      offset={4}
      onOptionSubmit={(placeId) => {
        const place = results.find((item) => item.place_id === placeId)
        if (place) choose(place)
      }}
    >
      <Combobox.Target>
        <TextInput
          label={label ?? t('editorV2.generation.advertiser.label')}
          withAsterisk
          placeholder={placeholder ?? t('editorV2.generation.advertiser.placeholder')}
          value={display}
          onChange={handleChange}
          onFocus={() => { if (state !== 'idle') combobox.openDropdown() }}
          onBlur={() => {
            combobox.closeDropdown()
            // Leaving the field without picking restores the current advertiser.
            if (value && state !== 'loading') setQuery('')
          }}
          disabled={disabled}
          autoComplete="off"
          // The chosen advertiser shows its brand mark; typing a new search shows the search icon.
          leftSection={value && !query
            ? <AdvertiserAvatar place={value} apiBase={apiBase} size={22} />
            : <IconSearch size={15} stroke={1.8} />}
          rightSection={showClear ? (
            <ActionIcon
              variant="subtle"
              color="dark"
              size="sm"
              aria-label={t('editorV2.generation.advertiser.clear')}
              onMouseDown={(event) => event.preventDefault()}
              onClick={clearAll}
            >
              <IconX size={16} stroke={1.8} />
            </ActionIcon>
          ) : null}
          data-selected={value ? 'true' : 'false'}
          error={error}
        />
      </Combobox.Target>

      <Combobox.Dropdown className={classes.dropdown}>
        {state === 'loading' && (
          <div className={classes.dropdownState} aria-busy="true">
            <Group gap={8} mb={8}>
              <Loader size={14} color="#d00b65" />
              <Text size="xs" c="dimmed">{t('editorV2.generation.advertiser.searching')}</Text>
            </Group>
            {[0, 1, 2].map((row) => (
              <div key={row} className={classes.skeletonRow}>
                <Skeleton circle height={42} width={42} />
                <div className={classes.skeletonLines}>
                  <Skeleton height={12} width={`${45 + row * 10}%`} radius="xl" />
                  <Skeleton height={10} width={`${70 - row * 8}%`} radius="xl" />
                </div>
              </div>
            ))}
          </div>
        )}

        {state === 'results' && (
          <Combobox.Options>
            {results.map((place) => {
              const chosen = Boolean(value?.place_id) && value.place_id === place.place_id
              return (
                <Combobox.Option
                  value={place.place_id}
                  key={place.place_id}
                  className={classes.option}
                  data-compact={compact ? 'true' : undefined}
                  data-chosen={chosen ? 'true' : undefined}
                >
                  {chosen && <IconCheck size={16} stroke={2} className={classes.optionCheck} aria-label={t('editorV2.generation.advertiser.selected')} />}
                  {!compact && <AdvertiserAvatar place={place} apiBase={apiBase} />}
                  <span className={classes.optionText}>
                    <span className={classes.optionName} data-part="name">{placeLabel(place)}</span>
                    <span className={classes.optionAddress} data-part="address">
                      {place.address || place.description || place.city || ''}
                    </span>
                  </span>
                </Combobox.Option>
              )
            })}
          </Combobox.Options>
        )}

        {state === 'empty' && (
          <div className={classes.emptyState}>
            <IconSearchOff size={24} stroke={1.6} />
            <div className={classes.emptyText}>
              <span className={classes.emptyTitle}>{t('editorV2.generation.advertiser.noneFound')}</span>
              <span className={classes.emptyHelp}>
                {t('editorV2.generation.advertiser.noneFoundHelp')}
              </span>
            </div>
            {onAddNew && (
              <button
                type="button"
                className={classes.addButton}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => { const typed = latestQuery.current; reset(); onAddNew(typed) }}
              >
                <IconPlus size={16} stroke={2} aria-hidden="true" />
                {addLabel ?? t('editorV2.generation.form.addNewAdvertiser')}
              </button>
            )}
          </div>
        )}

        {state === 'error' && (
          <div className={classes.errorState}>
            <IconAlertCircle size={18} />
            <div className={classes.errorText}>
              <Text size="sm" fw={600}>{t('editorV2.generation.advertiser.serviceError')}</Text>
              <Text size="xs" c="dimmed">{t('editorV2.generation.advertiser.serviceErrorHelp')}</Text>
            </div>
            <Button
              size="xs"
              radius="md"
              color="#d00b65"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => runSearch(latestQuery.current)}
            >
              {t('editorV2.generation.advertiser.retry')}
            </Button>
          </div>
        )}
      </Combobox.Dropdown>
    </Combobox>
  )
}
